(ns app.domain.user.authority
  (:require #?(:clj [core.db.entity :as entity])
            #?(:clj [core.util.util :as util])
            #?(:clj [datomic.api :as d])
            [app.domain.group.model :as group-model]))

#?(:clj
   (defn authority-groups-query [{:keys [user additional-in additional-where additional-args]}]
     (d/query {:query {:find  '[[?ag ...]]
                       :in    (concat '[$ ?user [?group-types ...]]
                                      additional-in)
                       :where (concat '[[?user :user/groups ?ug]
                                        ;; Find authority groups via direct parent OR via superdomain
                                        (or-join [?ag ?ug ?group-types]
                                                 ;; user-group -> authority-group (direct parent)
                                                 (and [?ug :entity/parent ?ag]
                                                      [?ag :group/type ?group-types])
                                                 ;; user-group -> superdomain -> domain -> authority-group
                                                 (and [?ug :entity/parent ?superdomain]
                                                      [?superdomain :group/type :group.type/superdomain]
                                                      [(tuple ?superdomain :entity.type/group) ?sd+t]
                                                      [?domain :entity/parent+type ?sd+t]
                                                      [?domain :group/type :group.type/domain]
                                                      [(tuple ?domain :entity.type/group) ?d+t]
                                                      [?ag :entity/parent+type ?d+t]
                                                      [?ag :group/type ?group-types]))]
                                      additional-where)}
               :args  (concat [(d/entity-db user)
                               (:db/id user)
                               [:group.type/application-authority :group.type/upper-tier-authority]]
                              additional-args)})))

(defn authority-groups
  "Given user entity, returns all application-authority groups that user belongs to.
   User directly belongs to sub-groups of authority-group."
  ([user]
   (authority-groups user true))
  ([user as-entities?]
   #?(:clj
      (if-let [db (and (entity/entity? user)
                       (d/entity-db user))]
        (cond->> (authority-groups-query (util/mmap user))
          as-entities? (map (partial d/entity db)))
        ;; Fallback to filtering :user/groups if the user is not a Datomic entity but a map
        (let [user-groups             (:user/groups user)
              ;; Find authority groups as direct parents
              direct-auth-groups      (->> user-groups
                                           (map :entity/parent)
                                           (filter group-model/application-or-upper-tier-authority?))
              ;; Find authority groups via superdomain
              superdomain-auth-groups (->> user-groups
                                           (map :entity/parent)
                                           (filter group-model/superdomain?)
                                           (mapcat group-model/descendant-authority-groups))]
          (distinct (concat direct-auth-groups superdomain-auth-groups))))
      :cljs
      (let [auth-groups (:user/authority-groups user)]
        (if as-entities?
          auth-groups
          (map :db/id auth-groups))))))

(defn authority-in-municipality?
  "Returns true if the given user is an authority in the given municipality"
  ([user municipality-entity]
   (let [[country number] (:municipality/country+number municipality-entity)]
     (authority-in-municipality? user country number)))
  ([user country municipality-number]
   (->> (authority-groups user)
        (mapcat group-model/scope-municipalities)
        (map :municipality/country+number)
        (some #(= [country municipality-number] %)))))

(defn authority-in-some-subgroup?
  "Returns true if the given user is an authority in some subgroup of the (domain) group"
  [user domain-group]
  (let [dg-ids (->> (authority-groups user)
                    (map (comp :db/id :entity/parent))
                    set)]
    (contains? dg-ids (:db/id domain-group))))

(defn authority-in-group?
  "Returns true if the given user is an authority in the authority-group"
  [user authority-group]
  (let [group-ids (->> (authority-groups user)
                       (map :db/id)
                       set)]
    (contains? group-ids (:db/id authority-group))))


(defn user-groups-providing-authority
  "Return user's groups that are sub-groups of the given authority group
  or its superdomain group, if such exists."
  [user auth-group]
  (let [user-groups (:user/groups user)]
    (concat (->> user-groups
                 (filter #(= (-> % :entity/parent :db/id)
                             (:db/id auth-group))))
            (when-let [superdomain (group-model/group-superdomain auth-group)]
              (->> user-groups
                   (filter #(= (-> % :entity/parent :db/id)
                               (:db/id superdomain))))))))

(defn admin-authority-in-group?
  "Returns true if the given user is an authority and an admin in the authority-group"
  [user authority-group]
  (let [authority-group-id (:db/id authority-group)]
    (->> (authority-groups user)
         (some #(and (:group-admin? %) (= authority-group-id (:db/id %))))
         boolean)))

(defn authority-in-some-group? [user]
  (-> (authority-groups user) seq boolean))

(defn- property-admin-domain-group-ids
  "Returns all domain group ids for which the user is a domain-admin or property-manager."
  [user]
  (->> user
       :user/groups
       (filter (some-fn (partial group-model/has-role? :group.role/property-management)
                        (partial group-model/has-role? :group.role/domain-admin)))
       (map (comp :db/id :entity/parent))
       distinct))

(defn- authority-parent-domain-group-ids
  "Returns all domain group ids for the user which are parents of user's authority groups."
  [user]
  (->> user
       authority-groups
       (map :entity/parent)
       (filter #(= (:group/type %) :group.type/domain))
       (map :db/id)
       distinct))

(defn property-domain-group-ids
  "Returns all domain group ids for the user:
  1. User is domain-admin/property-manager for that domain group
  OR
  2. User is an authority in an authority group which is a child of that domain group"
  [user]
  (let [admin-dg-ids     (property-admin-domain-group-ids user)
        ag-parent-dg-ids (authority-parent-domain-group-ids user)]
    (->> (concat admin-dg-ids ag-parent-dg-ids)
         distinct)))

(defn user-group-domain-group-ids
  "Returns domain group ids where the user is a member of a user group
   that is a child of the domain group (directly or via superdomain).
   When `roles` are given, only groups with at least one of those roles are considered.
   This covers e.g. meeting-coordinator user groups."
  ([user]
   (user-group-domain-group-ids user nil))
  ([user roles]
   (let [groups             (cond->> (:user/groups user)
                              (seq roles) (filter (fn [g] (some #(group-model/has-role? % g) roles))))
         parents            (map :entity/parent groups)
         direct-dg-ids      (->> parents
                                 (filter group-model/domain?)
                                 (map :db/id))
         superdomain-dg-ids (->> parents
                                 (filter group-model/superdomain?)
                                 distinct
                                 (mapcat group-model/child-groups)
                                 (filter group-model/domain?)
                                 (map :db/id))]
     (->> (concat direct-dg-ids superdomain-dg-ids)
          distinct))))

(defn authority-user? [user]
  (some #(->> (:entity/parent %)
              :group/type
              (contains? #{:group.type/application-authority
                           :group.type/upper-tier-authority
                           :group.type/domain
                           :group.type/superdomain}))
        (:user/groups user)))
